/**
 * IntakeFormRenderer
 *
 * Pure renderer for an IntakeFormTemplate schema. Used both for the
 * authoring preview pane in the builder AND the patient-facing CustomIntake
 * page — same code path so the author sees exactly what the patient gets.
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';
import { usStates } from '@/components/ui/state-input';
import { Star } from 'lucide-react';
import PhoneNumberInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import 'react-phone-number-input/style.css';
import { format, parse, isValid as isValidDate } from 'date-fns';
import type {
  IntakeQuestion,
  IntakeSection,
  ConditionalRule,
  AddressValue,
  FileUploadValue,
  FileTypeCategory,
} from '@/lib/intake/templates/types';
import { uploadIntakeFile } from '@/services/intakeFileUpload';
import {
  Camera,
  CheckCircle2,
  FileText,
  Loader2,
  Upload,
  X as XIcon,
} from 'lucide-react';

export type ResponseMap = Record<
  string,
  string | number | boolean | string[] | AddressValue | FileUploadValue | null | undefined
>;

const FILE_ACCEPT_BY_CATEGORY: Record<FileTypeCategory, string> = {
  any: 'image/jpeg,image/png,image/webp,image/heic,.pdf,.doc,.docx',
  image: 'image/jpeg,image/png,image/webp,image/heic',
  pdf: '.pdf,application/pdf',
  document: '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const FILE_MIME_BY_CATEGORY: Record<FileTypeCategory, (mime: string) => boolean> = {
  any: m => /^image\/(jpeg|png|webp|heic)$/.test(m) || m === 'application/pdf'
    || m === 'application/msword' || m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  image: m => /^image\/(jpeg|png|webp|heic)$/.test(m),
  pdf: m => m === 'application/pdf',
  document: m => m === 'application/pdf' || m === 'application/msword'
    || m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const isFileUploadValue = (v: unknown): v is FileUploadValue =>
  !!v && typeof v === 'object' && !Array.isArray(v) && 's3Key' in (v as object);

const EMPTY_ADDRESS: AddressValue = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  postal_code: '',
  full_address: '',
};

const isAddressValue = (v: unknown): v is AddressValue =>
  !!v && typeof v === 'object' && !Array.isArray(v) && 'address_line_1' in (v as object);

interface RendererProps {
  sections: IntakeSection[];
  responses: ResponseMap;
  onChange: (questionId: string, value: ResponseMap[string]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
}

function evalConditional(rule: ConditionalRule | undefined, responses: ResponseMap): boolean {
  if (!rule) return true;
  const v = responses[rule.questionId];
  switch (rule.operator) {
    case 'equals':
      // Multiselect answers are arrays — "equals X" reads as "X is among the
      // selections" rather than strict array-vs-scalar equality (which would
      // always be false).
      if (Array.isArray(v)) return v.includes(rule.value as string);
      return v === rule.value;
    case 'notEquals':
      if (Array.isArray(v)) return !v.includes(rule.value as string);
      return v !== rule.value;
    case 'contains':
      if (Array.isArray(v)) return v.includes(rule.value as string);
      return String(v ?? '').includes(String(rule.value));
    case 'greaterThan': return Number(v) > Number(rule.value);
    case 'lessThan': return Number(v) < Number(rule.value);
    default: return true;
  }
}

const QuestionField: React.FC<{
  question: IntakeQuestion;
  value: ResponseMap[string];
  onChange: (v: ResponseMap[string]) => void;
  disabled?: boolean;
  error?: string;
}> = ({ question, value, onChange, disabled, error }) => {
  const id = `q-${question.id}`;
  const inputType = question.inputType;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className={question.required ? 'required' : ''}>
        {question.text}
      </Label>
      {question.helpText && (
        <p className="text-xs text-slate-500">{question.helpText}</p>
      )}

      {inputType === 'text' && (
        <Input
          id={id}
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        />
      )}

      {inputType === 'textarea' && (
        <Textarea
          id={id}
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
        />
      )}

      {inputType === 'number' && (() => {
        const rules = question.validationRules || [];
        const minRule = rules.find(r => r.type === 'min');
        const maxRule = rules.find(r => r.type === 'max');
        const allowDecimals = question.allowDecimals === true;
        return (
          <Input
            id={id}
            type="number"
            min={minRule?.value !== undefined ? String(minRule.value) : undefined}
            max={maxRule?.value !== undefined ? String(maxRule.value) : undefined}
            step={allowDecimals ? 'any' : 1}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={e => {
              if (e.target.value === '') return onChange(null);
              const n = Number(e.target.value);
              if (Number.isNaN(n)) return onChange(null);
              onChange(allowDecimals ? n : Math.trunc(n));
            }}
            disabled={disabled}
          />
        );
      })()}

      {inputType === 'boolean' && (
        <RadioGroup
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          onValueChange={v => onChange(v === 'yes')}
          disabled={disabled}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-yes`} value="yes" />
            <Label htmlFor={`${id}-yes`} className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-no`} value="no" />
            <Label htmlFor={`${id}-no`} className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
      )}

      {inputType === 'select' && (
        <Select
          value={String(value ?? '')}
          onValueChange={v => onChange(v)}
          disabled={disabled}
        >
          <SelectTrigger id={id}>
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent>
            {(question.options || []).map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {inputType === 'multiselect' && (
        <div className="space-y-1.5">
          {(question.options || []).map(opt => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(opt);
            return (
              <div key={opt} className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-${opt}`}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={c => {
                    const next = c
                      ? Array.from(new Set([...arr, opt]))
                      : arr.filter(x => x !== opt);
                    onChange(next);
                  }}
                />
                <Label htmlFor={`${id}-${opt}`} className="font-normal cursor-pointer">
                  {opt}
                </Label>
              </div>
            );
          })}
        </div>
      )}

      {inputType === 'scale' && (() => {
        const min = Number.isFinite(question.scaleMin) ? (question.scaleMin as number) : 1;
        const rawMax = Number.isFinite(question.scaleMax) ? (question.scaleMax as number) : 5;
        const max = rawMax > min ? rawMax : min + 1;
        const steps: number[] = [];
        for (let n = min; n <= max; n += 1) steps.push(n);
        return (
          <div className="flex flex-wrap gap-2">
            {steps.map(n => (
              <button
                key={n}
                type="button"
                disabled={disabled}
                onClick={() => onChange(n)}
                className={`h-9 min-w-9 px-2 rounded-md border text-sm font-medium transition-colors ${
                  value === n
                    ? 'bg-brand-teal text-white border-brand-teal'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        );
      })()}

      {inputType === 'rating' && (() => {
        const rawMax = Number.isFinite(question.ratingMax) ? (question.ratingMax as number) : 5;
        const max = Math.min(10, Math.max(1, Math.trunc(rawMax)));
        const current = typeof value === 'number' ? value : 0;
        const steps: number[] = [];
        for (let n = 1; n <= max; n += 1) steps.push(n);
        return (
          <div className="flex flex-wrap gap-1">
            {steps.map(n => {
              const filled = n <= current;
              return (
                <button
                  key={n}
                  type="button"
                  disabled={disabled}
                  // Clicking the currently-selected star clears the rating,
                  // matching the typical star-rating affordance.
                  onClick={() => onChange(current === n ? null : n)}
                  aria-label={`Rate ${n} of ${max}`}
                  className="p-0.5 rounded transition-colors hover:bg-slate-50 disabled:opacity-50"
                >
                  <Star
                    className={`h-6 w-6 ${
                      filled
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        );
      })()}

      {inputType === 'date' && (
        <DatePicker
          placeholder="MM/DD/YYYY"
          value={
            typeof value === 'string' && value
              ? (() => {
                  const parsed = parse(value, 'yyyy-MM-dd', new Date());
                  return isValidDate(parsed) ? parsed : undefined;
                })()
              : undefined
          }
          onValueChange={d => {
            if (d && isValidDate(d)) onChange(format(d, 'yyyy-MM-dd'));
            else onChange('');
          }}
        />
      )}

      {inputType === 'time' && (
        <Input
          id={id}
          type="time"
          value={typeof value === 'string' ? value : ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        />
      )}

      {inputType === 'datetime' && (() => {
        // Split storage `YYYY-MM-DDTHH:mm` into a DatePicker (MM/DD/YYYY,
        // locale-independent) and a native time input. Native
        // `datetime-local` is browser-locale-dependent for the date part —
        // splitting it lets us force MM/DD/YYYY for the date portion.
        const raw = typeof value === 'string' ? value : '';
        const [datePart, timePart = ''] = raw.includes('T') ? raw.split('T') : [raw, ''];
        const dateValue = datePart
          ? (() => {
              const p = parse(datePart, 'yyyy-MM-dd', new Date());
              return isValidDate(p) ? p : undefined;
            })()
          : undefined;
        const emit = (nextDate: string, nextTime: string) => {
          if (!nextDate && !nextTime) return onChange('');
          if (!nextDate) return; // require a date before storing a time
          onChange(`${nextDate}T${nextTime || '00:00'}`);
        };
        return (
          <div className="flex gap-2">
            <div className="flex-1">
              <DatePicker
                placeholder="MM/DD/YYYY"
                value={dateValue}
                disabled={disabled}
                onValueChange={d => emit(d && isValidDate(d) ? format(d, 'yyyy-MM-dd') : '', timePart)}
              />
            </div>
            <Input
              type="time"
              value={timePart}
              onChange={e => emit(datePart, e.target.value)}
              disabled={disabled}
              className="w-32"
            />
          </div>
        );
      })()}

      {inputType === 'file' && (() => {
        const category: FileTypeCategory = (question.fileTypes as FileTypeCategory) || 'any';
        const maxMb = Math.min(25, Math.max(1, Math.trunc(question.fileMaxSizeMb ?? 15)));
        const accept = FILE_ACCEPT_BY_CATEGORY[category];
        const validateMime = FILE_MIME_BY_CATEGORY[category];
        const current = isFileUploadValue(value) ? value : null;
        return (
          <FileUploadField
            id={id}
            category={category}
            accept={accept}
            maxMb={maxMb}
            validateMime={validateMime}
            current={current}
            disabled={disabled}
            onChange={onChange}
          />
        );
      })()}

      {inputType === 'phone' && (
        <PhoneNumberInput
          international
          defaultCountry="US"
          countryCallingCodeEditable={false}
          countrySelectProps={{ disabled: true }}
          flags={flags}
          value={typeof value === 'string' ? value : ''}
          onChange={v => onChange(v ?? '')}
          disabled={disabled}
          placeholder="Enter phone number"
        />
      )}

      {inputType === 'address' && (() => {
        const addr: AddressValue = isAddressValue(value) ? value : EMPTY_ADDRESS;
        const update = (patch: Partial<AddressValue>) => onChange({ ...addr, ...patch });
        return (
          <div className="space-y-2">
            <PlacesAutocomplete
              label=""
              placeholder="Address line 1"
              disabled={disabled}
              value={addr.address_line_1}
              onSelectPlace={parsed => onChange(parsed)}
              onManualChange={v => update({ address_line_1: v, full_address: '' })}
            />
            <Input
              placeholder="Apartment, suite, etc. (optional)"
              value={addr.address_line_2}
              onChange={e => update({ address_line_2: e.target.value })}
              disabled={disabled}
            />
            <div className="grid grid-cols-3 gap-2">
              <Input
                placeholder="City"
                value={addr.city}
                onChange={e => update({ city: e.target.value })}
                disabled={disabled}
              />
              <Select
                value={addr.state || undefined}
                onValueChange={v => update({ state: v })}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {usStates.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="ZIP"
                value={addr.postal_code}
                onChange={e => update({ postal_code: e.target.value })}
                disabled={disabled}
              />
            </div>
          </div>
        );
      })()}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export const IntakeFormRenderer: React.FC<RendererProps> = ({
  sections, responses, onChange, disabled, errors = {},
}) => {
  return (
    <div className="space-y-8">
      {sections.map(section => (
        <section key={section.id} className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
            {section.description && (
              <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
            )}
          </div>

          <div className="space-y-5">
            {section.questions.map(q => {
              if (!evalConditional(q.conditionalOn, responses)) return null;
              return (
                <QuestionField
                  key={q.id}
                  question={q}
                  value={responses[q.id]}
                  onChange={v => onChange(q.id, v)}
                  disabled={disabled}
                  error={errors[q.id]}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

// Resize + JPEG-encode an image File before upload, matching the GLP-1
// intake's compression: max 1600×1200, quality 0.85. Non-image files are
// returned unchanged. Falls back to the original blob if anything fails.
async function compressImageBlob(file: File): Promise<{ blob: Blob; contentType: string; fileName: string }> {
  if (!file.type.startsWith('image/')) {
    return { blob: file, contentType: file.type, fileName: file.name };
  }
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1600;
        const MAX_H = 1200;
        let w = img.width;
        let h = img.height;
        if (w > MAX_W) { h = (h * MAX_W) / w; w = MAX_W; }
        if (h > MAX_H) { w = (w * MAX_H) / h; h = MAX_H; }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas unsupported'));
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => {
          if (!b) return reject(new Error('Compression failed'));
          const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
          resolve({ blob: b, contentType: 'image/jpeg', fileName: `${base}.jpg` });
        }, 'image/jpeg', 0.85);
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = URL.createObjectURL(file);
    });
  } catch {
    return { blob: file, contentType: file.type, fileName: file.name };
  }
}

const FileUploadField: React.FC<{
  id: string;
  category: FileTypeCategory;
  accept: string;
  maxMb: number;
  validateMime: (mime: string) => boolean;
  current: FileUploadValue | null;
  disabled?: boolean;
  onChange: (v: ResponseMap[string]) => void;
}> = ({ id, category, accept, maxMb, validateMime, current, disabled, onChange }) => {
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [localError, setLocalError] = React.useState<string | null>(null);

  // Drop the object URL when we unmount or pick a new file so we don't
  // leak blob references for the life of the page.
  React.useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|Android/i.test(navigator.userAgent);
  const allowsImages = category === 'image' || category === 'any';
  const hasUpload = !!current;
  const previewIsImage = previewUrl !== null
    || (current?.contentType ? current.contentType.startsWith('image/') : false);

  const handleFile = async (file: File) => {
    setLocalError(null);
    if (!validateMime(file.type)) {
      setLocalError(`File type "${file.type || 'unknown'}" is not allowed.`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setLocalError(`File is larger than ${maxMb} MB.`);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    setUploading(true);
    setProgress(5);
    try {
      const { blob, contentType, fileName } = await compressImageBlob(file);
      const uploaded = await uploadIntakeFile(blob, {
        fileName,
        contentType,
        onProgress: setProgress,
      });
      onChange(uploaded);
    } catch (e: unknown) {
      setLocalError(e instanceof Error ? e.message : 'Upload failed.');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setLocalError(null);
    setProgress(0);
    onChange(null);
  };

  const borderColor = localError
    ? 'border-red-400'
    : hasUpload
      ? 'border-emerald-400'
      : 'border-slate-300';

  return (
    <div className="space-y-2">
      <div className={`rounded-xl border ${borderColor} bg-white p-4 transition-colors`}>
        {/* Preview area */}
        <div className="flex min-h-[160px] items-center justify-center mb-4">
          {uploading && (
            <div className="text-center space-y-3 w-full max-w-[240px]">
              <Loader2 className="mx-auto h-9 w-9 animate-spin text-brand-teal" />
              <p className="text-sm text-slate-600">Uploading… {progress}%</p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-brand-teal transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {!uploading && previewIsImage && (previewUrl || hasUpload) && (
            <div className="relative">
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                aria-label="Remove file"
                className="absolute -right-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-white hover:bg-slate-800"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Upload preview"
                  className="max-h-[170px] w-auto rounded-lg object-cover"
                />
              ) : (
                <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>Uploaded</span>
                </div>
              )}
            </div>
          )}

          {!uploading && hasUpload && !previewIsImage && (
            <div className="relative w-full">
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                aria-label="Remove file"
                className="absolute -right-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-white hover:bg-slate-800"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3">
                <FileText className="h-6 w-6 flex-shrink-0 text-slate-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {current?.fileName}
                  </p>
                  <p className="text-xs text-emerald-600">Uploaded</p>
                </div>
              </div>
            </div>
          )}

          {!uploading && !hasUpload && !previewUrl && (
            <div className="flex h-[124px] w-[124px] items-center justify-center rounded-xl bg-slate-100">
              {allowsImages
                ? <Camera className="h-12 w-12 text-slate-400" />
                : <FileText className="h-12 w-12 text-slate-400" />}
            </div>
          )}
        </div>

        {/* Hidden inputs */}
        <input
          ref={uploadInputRef}
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          disabled={disabled || uploading}
          onChange={e => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) handleFile(f);
          }}
        />
        {allowsImages && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            disabled={disabled || uploading}
            onChange={e => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) handleFile(f);
            }}
          />
        )}

        {/* Action buttons */}
        {!hasUpload && !uploading && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              disabled={disabled}
              onClick={() => uploadInputRef.current?.click()}
              className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <Upload className="h-4 w-4" />
              Upload File
            </button>
            {allowsImages && isMobile && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => cameraInputRef.current?.click()}
                className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <Camera className="h-4 w-4" />
                Take Photo
              </button>
            )}
          </div>
        )}

        {localError && (
          <p className="mt-3 text-center text-sm text-red-500">{localError}</p>
        )}
      </div>

      <p className="text-xs text-slate-500">Max size: {maxMb} MB</p>
    </div>
  );
};

/**
 * Validate responses against the schema. Returns a map of question id → error.
 */
export function validateResponses(
  sections: IntakeSection[],
  responses: ResponseMap,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of sections) {
    for (const q of section.questions) {
      if (!evalConditional(q.conditionalOn, responses)) continue;
      const v = responses[q.id];
      if (q.required) {
        const empty = v === undefined || v === null || v === ''
          || (Array.isArray(v) && v.length === 0)
          || (isAddressValue(v) && !v.address_line_1 && !v.full_address)
          || (isFileUploadValue(v) && !v.s3Key);
        if (empty) {
          errors[q.id] = 'This field is required';
          continue;
        }
      }
      for (const rule of q.validationRules || []) {
        if (rule.type === 'min' && typeof v === 'number' && v < Number(rule.value)) errors[q.id] = rule.message;
        if (rule.type === 'max' && typeof v === 'number' && v > Number(rule.value)) errors[q.id] = rule.message;
        if (rule.type === 'minLength' && typeof v === 'string' && v.length < Number(rule.value)) errors[q.id] = rule.message;
        if (rule.type === 'maxLength' && typeof v === 'string' && v.length > Number(rule.value)) errors[q.id] = rule.message;
        if (rule.type === 'pattern' && typeof v === 'string' && rule.value && !new RegExp(String(rule.value)).test(v)) errors[q.id] = rule.message;
      }
      if (q.inputType === 'phone' && typeof v === 'string' && v && !isValidPhoneNumber(v, 'US')) {
        errors[q.id] = 'Please enter a valid US phone number.';
      }
    }
  }
  return errors;
}

export default IntakeFormRenderer;
